/**
 * The Portal's window — the WHEN sheet's model (handoff §5.5).
 *
 *   on      the switch. Off ⇒ live now, gone after four hours (the server's default).
 *   day     an index from today (0 = today).
 *   start   an hour of that day, or null = NOW (today only).
 *   endAbs  hours since today 00:00, so a window can span days.
 *
 * End ≥ start + 1h. The default end is start + 4h. Changing the day or the start keeps the
 * duration. Everything takes `now` so it can be tested on a fixed clock.
 */
export const DOW = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
export const MON = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
export const DEFAULT_HOURS = 4;
export const MIN_HOURS = 1;
/** The hour a window opens on a later day when nothing else was picked. */
export const DEFAULT_START_HOUR = 20;
/** The strips show two weeks; the calendar reaches twelve months. */
export const STRIP_DAYS = 14;
export const CALENDAR_MONTHS = 12;

export const DEFAULT_WINDOW = Object.freeze({ on: false, day: 0, start: null, endAbs: null });

/** Midnight at the start of day `i` from today. */
export function dayDate(i, now = new Date()) {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + i);
  return d;
}

/** `8 PM` · `12 AM`. */
export function fmtH(h) {
  const x = ((h % 24) + 24) % 24;
  return `${x % 12 || 12} ${x < 12 ? 'AM' : 'PM'}`;
}

/** `TODAY` · `TMRW` · `SAT OCT 3`. */
export function dShort(i, now = new Date()) {
  if (i === 0) return 'TODAY';
  if (i === 1) return 'TMRW';
  const d = dayDate(i, now);
  return `${DOW[d.getDay()]} ${MON[d.getMonth()]} ${d.getDate()}`;
}

/** `TODAY` · `TOMORROW` · `SAT OCT 3`. */
export function dayName(i, now = new Date()) {
  if (i === 0) return 'TODAY';
  if (i === 1) return 'TOMORROW';
  const d = dayDate(i, now);
  return `${DOW[d.getDay()]} ${MON[d.getMonth()]} ${d.getDate()}`;
}

/** `4H` · `1D` · `1D 6H`. */
export function lenText(hours) {
  if (hours < 24) return `${hours}H`;
  const d = Math.floor(hours / 24);
  const h = hours % 24;
  return h ? `${d}D ${h}H` : `${d}D`;
}

/** The start in hours since today 00:00. NOW counts as the current hour. */
export function startAbs(w, now = new Date()) {
  return (w.day || 0) * 24 + (w.start ?? now.getHours());
}

/** The end in hours since today 00:00, always at least an hour after the start. */
export function endAbs(w, now = new Date()) {
  const s = startAbs(w, now);
  return Math.max(s + MIN_HOURS, w.endAbs ?? s + DEFAULT_HOURS);
}

/** The pieces a label or a sheet needs, computed once. */
export function resolve(w, now = new Date()) {
  const s = startAbs(w, now);
  const e = endAbs(w, now);
  return {
    startAbs: s,
    endAbs: e,
    len: e - s,
    day: w.day || 0,
    start: w.start ?? null,
    eDay: Math.floor(e / 24),
    eHour: e % 24,
  };
}

/** A new window with the start moved and the duration kept. */
export function withStart(w, day, start, now = new Date()) {
  const { len } = resolve(w, now);
  const next = { ...w, day, start: day === 0 ? start : (start ?? DEFAULT_START_HOUR) };
  return { ...next, endAbs: startAbs(next, now) + len };
}

/** A new window ending on day `eDay` at the current end hour (or `hour`). */
export function withEnd(w, eDay, hour = null, now = new Date()) {
  const r = resolve(w, now);
  const h = hour ?? r.eHour;
  return { ...w, endAbs: Math.max(r.startAbs + MIN_HOURS, eDay * 24 + h) };
}

/** `NOW → 4H` off; `NOW → 11 PM`; `SAT OCT 3 · 8 PM → 12 AM`; `SAT OCT 3 8 PM → MON OCT 5 2 AM`. */
export function windowLabel(w, now = new Date()) {
  if (!w?.on) return 'NOW → 4H';
  const r = resolve(w, now);
  if (r.start == null) return `NOW → ${r.eDay === r.day ? '' : `${dShort(r.eDay, now)} `}${fmtH(r.eHour)}`;
  if (r.eDay === r.day) return `${dayName(r.day, now)} · ${fmtH(r.start)} → ${fmtH(r.eHour)}`;
  return `${dShort(r.day, now)} ${fmtH(r.start)} → ${dShort(r.eDay, now)} ${fmtH(r.eHour)}`;
}

/** `4H LEFT` · `2D 6H LEFT` · `OPENS SAT 8 PM` — the header's and the guest's short form. */
export function windowShort(w, now = new Date()) {
  if (!w?.on) return '4H LEFT';
  const r = resolve(w, now);
  if (r.start == null) return `${lenText(r.len)} LEFT`;
  const d = dayDate(r.day, now);
  return `OPENS ${r.day < 2 ? dayName(r.day, now) : DOW[d.getDay()]} ${fmtH(r.start)}`;
}

/** The help line under the switch. */
export function windowHelp(w, now = new Date()) {
  return w?.on ? windowLabel(w, now) : 'Without a window it goes live now and disappears after 4 hours.';
}

/** `SAT OCT 3 · 2 AM · 1D 6H` — the ENDS row's own label. */
export function endName(w, now = new Date()) {
  const r = resolve(w, now);
  return `${dShort(r.eDay, now)} · ${fmtH(r.eHour)} · ${lenText(r.len)}`;
}

/** What the API gets: ISO moments, or nulls when the switch is off. */
export function windowIso(w, now = new Date()) {
  if (!w?.on) return { startUtc: null, endUtc: null };
  const r = resolve(w, now);
  const start = r.start == null ? new Date(now) : new Date(dayDate(r.day, now).getTime() + r.start * 3600000);
  const end = new Date(dayDate(0, now).getTime() + r.endAbs * 3600000);
  return { startUtc: start.toISOString(), endUtc: end.toISOString() };
}

/** The month/day/time the venue's DATE style prints: the Portal's own window. */
export function windowDateBits(w, now = new Date()) {
  const r = resolve(w, now);
  const nowish = r.day === 0 && r.start == null;
  const d = dayDate(r.day, now);
  return {
    mon: nowish ? 'TODAY' : MON[d.getMonth()],
    day: nowish ? '●' : String(d.getDate()),
    time: r.start == null ? 'NOW' : fmtH(r.start),
  };
}

/** The STARTS hours: 8 AM–11 PM, and today only the ones still to come. */
export function startHours(day, now = new Date()) {
  const h = now.getHours();
  const out = [];
  for (let x = 8; x <= 23; x += 1) if (day > 0 || x > h) out.push(x);
  return out;
}

/** The day strip: two weeks from `from`, plus the chosen day if it lies beyond. */
export function stripDays(from, chosen) {
  const days = Array.from({ length: STRIP_DAYS }, (_, i) => from + i);
  if (chosen != null && chosen > from + STRIP_DAYS - 1) days.push(chosen);
  return days;
}

/** How many months from this month `dayIndex` falls in — where a calendar should open. */
export function monthOffsetOf(dayIndex, now = new Date()) {
  const t = dayDate(dayIndex, now);
  const n = dayDate(0, now);
  return Math.max(0, Math.min(CALENDAR_MONTHS, (t.getFullYear() - n.getFullYear()) * 12 + t.getMonth() - n.getMonth()));
}

/**
 * One month of calendar cells. `minDay` is the first pickable day index (0 for the start,
 * the start day for the end). Cells before the first weekday are blank.
 */
export function calendarCells(monthOffset, { selected = 0, minDay = 0, now = new Date() } = {}) {
  const n = dayDate(0, now);
  const first = new Date(n.getFullYear(), n.getMonth() + monthOffset, 1);
  const count = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < first.getDay(); i += 1) cells.push({ blank: true, key: `b${i}` });
  for (let d = 1; d <= count; d += 1) {
    const dt = new Date(first.getFullYear(), first.getMonth(), d);
    const index = Math.round((dt - n) / 864e5);
    cells.push({ key: `d${d}`, n: d, index, past: index < minDay, today: index === 0, selected: index === selected });
  }
  return { title: `${MON[first.getMonth()]} ${first.getFullYear()}`, cells };
}

/** The venue DATE bits for a live Portal: TODAY · ● · NOW once it has started, else its start. */
export function windowBitsFromIso(startIso, now = new Date()) {
  const d = startIso ? new Date(startIso) : null;
  if (!d || Number.isNaN(d.getTime()) || d.getTime() <= now.getTime() + 60000) return { mon: 'TODAY', day: '●', time: 'NOW' };
  const h = d.getHours();
  const m = d.getMinutes();
  const time = `${h % 12 || 12}${m ? `:${String(m).padStart(2, '0')}` : ''} ${h < 12 ? 'AM' : 'PM'}`;
  return { mon: MON[d.getMonth()], day: String(d.getDate()), time };
}
